import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    Briefcase,
    CheckCircle2,
    ClipboardList,
    Compass,
    Download,
    Microscope,
    Palette,
    Users,
    Wrench
} from 'lucide-react';
import { buildFullCareerReport } from './fullReportData';
import FullResultsPrintPage from './FullResultsPrintPage';

const ORIENTATION_ICONS = {
    R: Wrench,
    I: Microscope,
    A: Palette,
    S: Users,
    E: Briefcase,
    C: ClipboardList
};

const choiceToneClasses = {
    'Self-Driven': 'border-[#e9dcfb] bg-[#faf5ff] text-[#7c3aed]',
    'Seek out New': 'border-[#dbeafe] bg-[#eff6ff] text-[#2563eb]',
    'Others-Driven': 'border-[#e5e7eb] bg-[#f8fafc] text-[#475569]',
    Calculated: 'border-[#dcfce7] bg-[#f0fdf4] text-[#15803d]',
    Rational: 'border-[#dbeafe] bg-[#eff6ff] text-[#1d4ed8]',
    Emotional: 'border-[#ffedd5] bg-[#fff7ed] text-[#ea580c]',
    Positive: 'border-[#dcfce7] bg-[#f0fdf4] text-[#15803d]',
    Neutral: 'border-[#fef3c7] bg-[#fffbeb] text-[#b45309]',
    Negative: 'border-[#fee2e2] bg-[#fef2f2] text-[#dc2626]',
    'Optimal Usage': 'border-[#dcfce7] bg-[#f0fdf4] text-[#15803d]',
    'Under Usage': 'border-[#fef3c7] bg-[#fffbeb] text-[#b45309]',
    'Over Usage': 'border-[#fee2e2] bg-[#fef2f2] text-[#dc2626]'
};

const gradientHeadingStyle = {
    background: 'var(--gradient-text)',
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    color: 'transparent',
    lineHeight: 1.02
};

const SectionHeading = ({ eyebrow, title, intro }) => (
    <div className="mb-8">
        <span className="inline-flex items-center rounded-full border border-[#eadff0] bg-white/80 px-4 py-1.5 text-xs font-black uppercase tracking-[0.22em] text-[#92278F]">
            {eyebrow}
        </span>
        <h2 className="mt-[30px] text-3xl md:text-5xl font-bold text-[#171717] brand-font leading-tight">
            {title}
        </h2>
        <p className="mt-4 max-w-3xl text-base md:text-lg leading-relaxed text-[var(--color-subtext)] brand-text">
            {intro}
        </p>
    </div>
);

const FullResultGate = ({ formData, isSubmitting, onChange, onSubmit }) => {
    return (
        <div className="flex flex-col items-center justify-center min-h-[80vh] px-4 w-full animate-fadeIn pb-20 pt-5">
            <div className="w-full max-w-md bg-white shadow-xl rounded-3xl p-8 relative overflow-hidden">
                <div className="absolute top-0 left-0 h-3 w-full bg-gradient-to-r from-[#F37741] to-[#92278F]"></div>
                <h2 className="mt-4 text-2xl font-bold text-center text-[#2d3748] brand-font">Unlock your full career report</h2>
                <p className="mt-3 mb-8 text-center text-gray-500 brand-text leading-relaxed">
                    Please enter your details to view the updated premium report with distinct Part A and Part B insights.
                </p>

                <form onSubmit={onSubmit} className="flex flex-col gap-4">
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1">Name</label>
                        <input
                            required
                            name="name"
                            value={formData.name}
                            onChange={onChange}
                            className="w-full rounded-xl border border-gray-300 p-3 outline-none transition-all focus:border-[#92278F] focus:ring-1 focus:ring-[#92278F]"
                            placeholder="Your Name"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1">Phone Number</label>
                        <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 border-r border-gray-300 pr-2 font-bold text-gray-500">+91</span>
                            <input
                                required
                                type="tel"
                                name="phone"
                                value={formData.phone}
                                onChange={onChange}
                                className="w-full rounded-xl border border-gray-300 p-3 pl-14 outline-none transition-all focus:border-[#92278F] focus:ring-1 focus:ring-[#92278F]"
                                placeholder="Your Phone Number"
                                maxLength={10}
                            />
                        </div>
                    </div>

                    <div className="flex gap-4">
                        <div className="w-1/2">
                            <label className="block text-sm font-bold text-gray-700 mb-1">Age</label>
                            <input
                                required
                                type="number"
                                name="age"
                                value={formData.age}
                                onChange={onChange}
                                className="w-full rounded-xl border border-gray-300 p-3 outline-none transition-all focus:border-[#92278F] focus:ring-1 focus:ring-[#92278F]"
                                placeholder="Age"
                            />
                        </div>
                        <div className="w-1/2">
                            <label className="block text-sm font-bold text-gray-700 mb-1">Gender</label>
                            <select
                                required
                                name="gender"
                                value={formData.gender}
                                onChange={onChange}
                                className="w-full rounded-xl border border-gray-300 bg-white p-3 outline-none transition-all focus:border-[#92278F] focus:ring-1 focus:ring-[#92278F]"
                            >
                                <option value="">Select</option>
                                <option value="Male">Male</option>
                                <option value="Female">Female</option>
                                <option value="Other">Other</option>
                            </select>
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className={`mt-6 w-full rounded-xl py-3 text-lg font-bold text-white shadow-md transition-transform active:scale-95 ${isSubmitting ? 'bg-gray-400 cursor-wait' : 'bg-gradient-to-r from-[#F37741] to-[#92278F] hover:shadow-lg'}`}
                    >
                        {isSubmitting ? 'Generating Report...' : 'View Full Report'}
                    </button>
                </form>
            </div>
        </div>
    );
};

const RiasecRadar = ({ entries, dominantOrientation }) => {
    const size = 360;
    const center = size / 2;
    const maxRadius = 124;
    const gridLevels = [0.25, 0.5, 0.75, 1];

    const pointAt = (radius, index) => {
        const angle = ((-90 + index * 60) * Math.PI) / 180;

        return {
            x: center + Math.cos(angle) * radius,
            y: center + Math.sin(angle) * radius
        };
    };

    const gridPolygons = gridLevels.map((level) => (
        entries
            .map((_, index) => {
                const point = pointAt(maxRadius * level, index);
                return `${point.x},${point.y}`;
            })
            .join(' ')
    ));

    const dataPolygon = entries
        .map((entry, index) => {
            const point = pointAt((entry.score / 10) * maxRadius, index);
            return `${point.x},${point.y}`;
        })
        .join(' ');

    return (
        <div className="relative mx-auto flex aspect-square w-full max-w-[430px] items-center justify-center rounded-[2rem] border border-[#efe6dd] bg-gradient-to-br from-white via-[#fcfbff] to-[#fff7f2] p-6 shadow-[0_18px_60px_rgba(146,39,143,0.08)] full-report-card full-report-bg-soft">
            <svg viewBox={`0 0 ${size} ${size}`} className="h-full w-full">
                {gridPolygons.map((points, index) => (
                    <polygon
                        key={`grid-${index}`}
                        points={points}
                        fill="none"
                        stroke="#eadff0"
                        strokeWidth="1.4"
                    />
                ))}

                {entries.map((entry, index) => {
                    const point = pointAt(maxRadius, index);

                    return (
                        <line
                            key={`axis-${entry.code}`}
                            x1={center}
                            y1={center}
                            x2={point.x}
                            y2={point.y}
                            stroke="#eadff0"
                            strokeWidth="1.2"
                        />
                    );
                })}

                <polygon points={dataPolygon} fill="rgba(146,39,143,0.15)" stroke="#92278F" strokeWidth="3.5" />

                {entries.map((entry, index) => {
                    const point = pointAt((entry.score / 10) * maxRadius, index);

                    return (
                        <circle
                            key={`dot-${entry.code}`}
                            cx={point.x}
                            cy={point.y}
                            r="6"
                            fill={entry.color}
                            stroke="#ffffff"
                            strokeWidth="3"
                        />
                    );
                })}
            </svg>

            {entries.map((entry, index) => {
                const point = pointAt(maxRadius + 36, index);

                return (
                    <div
                        key={`label-${entry.code}`}
                        className="absolute w-[86px] rounded-2xl border border-white bg-white/95 px-2 py-2 text-center shadow-md"
                        style={{
                            left: `${(point.x / size) * 100}%`,
                            top: `${(point.y / size) * 100}%`,
                            transform: 'translate(-50%, -50%)'
                        }}
                    >
                        <p className="text-[10px] font-black uppercase tracking-[0.18em]" style={{ color: entry.color }}>
                            {entry.code}
                        </p>
                        <p className="mt-1 text-[11px] font-bold leading-tight text-[#171717] brand-text">
                            {entry.title}
                        </p>
                        <p className="mt-1 text-[10px] font-semibold text-gray-500">{entry.percent}%</p>
                    </div>
                );
            })}

            <div className="pointer-events-none absolute inset-0 flex items-center justify-center px-10">
                <div className="rounded-[1.6rem] border border-white bg-white/92 px-5 py-4 text-center shadow-lg">
                    <p className="text-[10px] font-black uppercase tracking-[0.22em] text-[#8a7d74]">Strongest orientation</p>
                    <p className="mt-2 text-lg font-black text-[#171717] brand-font">{dominantOrientation.title}</p>
                    <p className="mt-1 text-xs leading-relaxed text-gray-500 brand-text">Holland / RIASEC interest-vocation fit</p>
                </div>
            </div>
        </div>
    );
};

const SnapshotCard = ({ label, value, helper, icon: Icon, accentColor }) => (
    <div className="rounded-[1.5rem] border border-[#efe6dd] bg-white/92 p-4 shadow-sm full-report-card">
        <div className="flex items-start gap-3">
            <div
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border bg-white"
                style={{ borderColor: `${accentColor}35` }}
            >
                <Icon size={20} color={accentColor} strokeWidth={2.3} />
            </div>
            <div className="min-w-0">
                <p className="text-[11px] font-black uppercase tracking-[0.2em] text-[#8a7d74]">{label}</p>
                <p className="mt-1 text-lg font-black leading-tight text-[#171717] brand-font">{value}</p>
                <p className="mt-1 text-sm leading-relaxed text-gray-500 brand-text">{helper}</p>
            </div>
        </div>
    </div>
);

const OrientationCard = ({ entry }) => {
    const Icon = ORIENTATION_ICONS[entry.code];

    return (
        <div className="rounded-[1.75rem] border border-[#efe6dd] bg-white/90 p-5 shadow-sm full-report-card">
            <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-4">
                    <div
                        className="flex h-12 w-12 items-center justify-center rounded-2xl border bg-white"
                        style={{ borderColor: `${entry.color}50` }}
                    >
                        <Icon size={22} color={entry.color} strokeWidth={2.3} />
                    </div>

                    <div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-lg font-black text-[#171717] brand-font">{entry.title}</p>
                            <span className="rounded-full bg-[#f8f3fb] px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-[#92278F]">
                                #{entry.rank}
                            </span>
                        </div>
                        <p className="mt-1 text-sm font-semibold text-gray-500 brand-text">{entry.descriptor}</p>
                    </div>
                </div>

                <div className="text-right">
                    <p className="text-xl font-black" style={{ color: entry.color }}>{entry.percent}%</p>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-400">fit signal</p>
                </div>
            </div>

            <div className="mt-4 h-2.5 overflow-hidden rounded-full bg-[#f3eef4]">
                <div
                    className="h-full rounded-full"
                    style={{
                        width: `${Math.max(entry.percent, 6)}%`,
                        background: `linear-gradient(90deg, ${entry.color}, ${entry.color}cc)`
                    }}
                ></div>
            </div>

            <p className="mt-4 text-sm leading-relaxed text-[var(--color-subtext)] brand-text">{entry.summary}</p>

            <div className="mt-4 rounded-[1.1rem] border border-[#efe6dd] bg-[#fffdfb] px-4 py-3">
                <p className="text-[11px] font-black uppercase tracking-[0.18em] text-[#8a7d74]">Visible example career</p>
                <p className="mt-1 text-base font-black text-[#171717] brand-font">{entry.careerExample}</p>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
                {entry.strengths.map((strength) => (
                    <span
                        key={`${entry.code}-${strength}`}
                        className="rounded-full border px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em]"
                        style={{
                            borderColor: `${entry.color}30`,
                            color: entry.color,
                            backgroundColor: `${entry.color}12`
                        }}
                    >
                        {strength}
                    </span>
                ))}
            </div>
        </div>
    );
};

const ResolvedChoiceCard = ({ choice }) => {
    const toneClasses = choiceToneClasses[choice.label] || 'border-[#eadff0] bg-[#fcf8ff] text-[#92278F]';

    return (
        <div className="rounded-[1.65rem] border border-[#efe6dd] bg-white/92 p-5 shadow-sm full-report-card">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <p className="text-lg font-black text-[#171717] brand-font">{choice.pillar}</p>
                    <p className="mt-1 text-sm font-semibold text-gray-500 brand-text">{choice.descriptor}</p>
                </div>
                <span className="rounded-full border border-[#efe6dd] bg-[#fffdfb] px-3 py-1 text-[10px] font-black uppercase tracking-[0.16em] text-[#8a7d74]">
                    {choice.emphasis}
                </span>
            </div>

            <div className="mt-4 flex items-center justify-between gap-3">
                <span className={`inline-flex rounded-full border px-3 py-1 text-[11px] font-black uppercase tracking-[0.18em] ${toneClasses}`}>
                    {choice.label}
                </span>
                <span className="text-sm font-bold text-gray-400">{choice.percent}%</span>
            </div>

            <p className="mt-4 text-sm leading-relaxed text-[var(--color-subtext)] brand-text">{choice.summary}</p>
        </div>
    );
};

const ActionStepCard = ({ step }) => (
    <div className="rounded-[1.75rem] border border-[#efe6dd] bg-white/92 p-5 shadow-sm full-report-card">
        <div className="flex items-start gap-4">
            <div className="mt-1 flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-br from-[#fff2eb] to-[#f8f0ff] text-[#92278F]">
                <CheckCircle2 size={20} strokeWidth={2.3} />
            </div>
            <div>
                <p className="text-xl font-black text-[#171717] brand-font">{step.title}</p>
                <p className="mt-2 text-sm leading-relaxed text-[var(--color-subtext)] brand-text">{step.text}</p>
            </div>
        </div>
    </div>
);

const FullResultsPage = ({ scores, quizData }) => {
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isPreparingPrint, setIsPreparingPrint] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        age: '',
        gender: ''
    });

    const report = useMemo(() => buildFullCareerReport({ scores, quizData }), [scores, quizData]);
    const printDocumentTitle = useMemo(
        () => `${(formData.name || 'Yancha').replace(/\s+/g, '_')}_Career_Report`,
        [formData.name]
    );

    useEffect(() => {
        if (typeof window === 'undefined') {
            return undefined;
        }

        const handleAfterPrint = () => {
            setIsPreparingPrint(false);
        };

        window.addEventListener('afterprint', handleAfterPrint);

        return () => {
            window.removeEventListener('afterprint', handleAfterPrint);
        };
    }, []);

    useEffect(() => {
        if (!isPreparingPrint || typeof document === 'undefined') {
            return undefined;
        }

        const previousTitle = document.title;
        document.title = printDocumentTitle;

        return () => {
            document.title = previousTitle;
        };
    }, [isPreparingPrint, printDocumentTitle]);

    const handlePrint = () => {
        if (isPreparingPrint || typeof window === 'undefined') {
            return;
        }

        setIsPreparingPrint(true);

        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
                window.print();
            });
        });
    };

    const printStyles = useMemo(() => `
        @page {
            size: A4 portrait;
            margin: 12mm;
        }

        @media screen {
            .full-results-dedicated-print-portal {
                display: none !important;
            }
        }

        @media print {
            * {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color-adjust: exact !important;
            }

            html,
            body {
                background: #ffffff !important;
                margin: 0 !important;
                padding: 0 !important;
                width: auto !important;
                min-width: 0 !important;
            }

            body > *:not(.full-results-dedicated-print-portal) {
                display: none !important;
            }

            .full-results-dedicated-print-portal {
                display: block !important;
                visibility: visible !important;
                position: static !important;
                width: 100% !important;
                padding: 0 !important;
                background: #ffffff !important;
            }

            .full-results-dedicated-print-portal,
            .full-results-dedicated-print-portal * {
                visibility: visible !important;
            }

            .full-results-print-toolbar,
            .full-results-live-page {
                display: none !important;
            }

            .full-results-dedicated-print-surface {
                width: 186mm !important;
                margin: 0 auto !important;
            }

            .full-results-dedicated-print-page {
                break-after: page !important;
                page-break-after: always !important;
                overflow: visible !important;
                box-shadow: none !important;
            }

            .full-results-dedicated-print-page:last-child {
                break-after: auto !important;
                page-break-after: auto !important;
            }

            .full-results-dedicated-print-section,
            .full-results-dedicated-print-grid,
            .full-results-dedicated-print-card {
                break-inside: avoid !important;
                page-break-inside: avoid !important;
            }
        }
    `, []);

    const handleChange = (event) => {
        const { name, value } = event.target;

        if (name === 'phone') {
            const numbersOnly = value.replace(/[^0-9]/g, '').slice(0, 10);
            setFormData((previous) => ({ ...previous, [name]: numbersOnly }));
            return;
        }

        setFormData((previous) => ({ ...previous, [name]: value }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (formData.phone.length !== 10) {
            alert('Please enter a valid 10-digit phone number.');
            return;
        }

        setIsSubmitting(true);

        try {
            const payload = {
                post_name: formData.phone,
                json_body: {
                    ...formData,
                    scores
                }
            };

            await fetch('https://yanchacms.metadei.in/wp-json/career-demo/v1/create', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            setFormSubmitted(true);
        } catch (error) {
            console.error('Submission failed', error);
            setFormSubmitted(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!formSubmitted) {
        return (
            <FullResultGate
                formData={formData}
                isSubmitting={isSubmitting}
                onChange={handleChange}
                onSubmit={handleSubmit}
            />
        );
    }

    const participantName = formData.name?.trim() || 'Your';
    const participantHeading = participantName === 'Your' ? 'Your' : `${participantName}'s`;
    const IconForDominant = ORIENTATION_ICONS[report.dominantOrientation.code] || Compass;
    const resolvedChoices = report.partB.resolvedChoices;
    const liveActionPlan = report.liveActionPlan || [];
    const printSurface = typeof document !== 'undefined' && document.body
        ? createPortal(
            <FullResultsPrintPage
                report={report}
                participantHeading={participantHeading}
                participantName={participantName}
            />,
            document.body
        )
        : null;

    return (
        <>
            <style>{printStyles}</style>

            <div className="full-results-live-page full-report-shell flex flex-col items-center justify-center min-h-[80vh] w-full px-4 pb-20 pt-5 animate-fadeIn">
                <div className="w-full max-w-7xl mx-auto">
                    <div className="full-results-print-toolbar mb-6 flex justify-stretch sm:mb-8 sm:justify-end">
                        <button
                            type="button"
                            onClick={handlePrint}
                            disabled={isPreparingPrint}
                            className="flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-purple-600 to-orange-500 px-6 py-3 font-bold text-white shadow-lg transition-all hover:-translate-y-0.5 hover:from-purple-700 hover:to-orange-600 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
                        >
                            <Download size={20} />
                            <span>{isPreparingPrint ? 'Opening print preview…' : 'Download Full Report (PDF)'}</span>
                        </button>
                    </div>

                    <div className="space-y-8">
                        <section className="overflow-hidden rounded-[2rem] border border-[#efe6dd] bg-white/70 p-6 md:p-10 shadow-[0_22px_70px_rgba(0,0,0,0.06)] backdrop-blur-xl full-report-card">
                            <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr] xl:items-start">
                                <div>
                                    <span className="inline-flex items-center rounded-full border border-[#eadff0] bg-white/80 px-4 py-1.5 text-xs font-black uppercase tracking-[0.22em] text-[#92278F]">
                                        Live full report
                                    </span>
                                    <h1
                                        className="mt-[30px] max-w-full text-[1.9rem] font-bold brand-font tracking-tight leading-[0.96] sm:max-w-[12ch] sm:text-[2.45rem] md:text-[3.1rem] lg:text-[3.6rem]"
                                        style={gradientHeadingStyle}
                                    >
                                        <span className="block">{participantHeading}</span>
                                        <span className="block">full career report</span>
                                    </h1>
                                    <div className="mt-6 h-[3px] w-24 rounded-full bg-gradient-to-r from-[var(--color-brand)] to-[var(--color-highlight)]"></div>
                                    <p className="mt-6 max-w-3xl text-base leading-relaxed text-[var(--color-subtext)] brand-text md:text-xl">
                                        This live report keeps Part A and Part B clearly separate: Part A shows all six RIASEC orientations, and Part B resolves your categorical career-choice pattern more directly.
                                    </p>

                                    <div className="mt-6 flex flex-wrap gap-3">
                                        <span className="inline-flex items-center rounded-full border border-[#eadff0] bg-white px-4 py-2 text-sm font-bold text-[#92278F]">
                                            Interest code: {report.interestCode}
                                        </span>
                                        <span className="inline-flex items-center rounded-full border border-[#f2dccf] bg-white px-4 py-2 text-sm font-bold text-[#F37741]">
                                            Strongest Part A: {report.dominantOrientation.title}
                                        </span>
                                        <span className="inline-flex items-center rounded-full border border-[#eee7df] bg-white px-4 py-2 text-sm font-bold text-[#171717]">
                                            Part B recommendation: {report.partB.recommendation.title}
                                        </span>
                                    </div>
                                </div>

                                <div className="grid gap-4">
                                    <SnapshotCard
                                        label="Dominant Part A"
                                        value={report.dominantOrientation.title}
                                        helper={`${report.dominantOrientation.percent}% orientation fit`}
                                        icon={IconForDominant}
                                        accentColor={report.dominantOrientation.color}
                                    />
                                    <SnapshotCard
                                        label="Part B recommendation"
                                        value={report.partB.recommendation.title}
                                        helper={report.partB.recommendation.matrixKey}
                                        icon={Compass}
                                        accentColor="#92278F"
                                    />
                                    <SnapshotCard
                                        label="Foundation + execution"
                                        value={`${resolvedChoices.attitude.label} attitude · ${resolvedChoices.resources.label} resources`}
                                        helper="Attitude is the foundation. Resources influence how well intent turns into action."
                                        icon={ClipboardList}
                                        accentColor="#F37741"
                                    />
                                </div>
                            </div>
                        </section>

                        <section className="rounded-[2rem] border border-[#efe6dd] bg-white/70 p-6 md:p-10 shadow-[0_22px_70px_rgba(0,0,0,0.06)] backdrop-blur-xl full-report-card">
                            <SectionHeading
                                eyebrow="Part A | Holland / RIASEC"
                                title="Personality-interest-vocation fit"
                                intro="Part A keeps all six RIASEC orientations visible, while adding one approved example career for each orientation so the output stays clear and easier to scan."
                            />

                            <div className="grid gap-8 xl:grid-cols-[0.98fr_1.02fr] xl:items-start">
                                <RiasecRadar entries={report.riasecEntries} dominantOrientation={report.dominantOrientation} />

                                <div className="space-y-4">
                                    <div className="rounded-[1.8rem] border border-[#eadff0] bg-gradient-to-br from-[#fcf8ff] to-white p-6 shadow-sm full-report-card">
                                        <div className="flex flex-wrap items-center gap-3">
                                            <span className="rounded-full bg-[#f6edfb] px-3 py-1 text-[11px] font-black uppercase tracking-[0.18em] text-[#92278F]">
                                                Strongest orientation
                                            </span>
                                            <span className="rounded-full bg-[#fff3ea] px-3 py-1 text-[11px] font-black uppercase tracking-[0.18em] text-[#F37741]">
                                                Top 3 code: {report.interestCode}
                                            </span>
                                        </div>

                                        <div className="mt-4 flex items-start gap-4">
                                            <div
                                                className="flex h-14 w-14 items-center justify-center rounded-2xl border bg-white"
                                                style={{ borderColor: `${report.dominantOrientation.color}45` }}
                                            >
                                                <IconForDominant size={28} color={report.dominantOrientation.color} strokeWidth={2.3} />
                                            </div>
                                            <div>
                                                <h3 className="text-2xl font-black text-[#171717] brand-font">{report.dominantOrientation.title}</h3>
                                                <p className="mt-1 text-sm font-semibold text-gray-500 brand-text">{report.dominantOrientation.descriptor}</p>
                                                <p className="mt-3 text-sm leading-relaxed text-[var(--color-subtext)] brand-text">{report.partA.summary}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="rounded-[1.6rem] border border-[#efe6dd] bg-white/92 p-5 shadow-sm full-report-card">
                                        <div className="grid gap-4 md:grid-cols-2">
                                            <div>
                                                <p className="text-sm font-black uppercase tracking-[0.2em] text-[#8a7d74]">Work environment fit</p>
                                                <p className="mt-3 text-base leading-relaxed text-[var(--color-subtext)] brand-text">{report.partA.environmentSummary}</p>
                                            </div>
                                            <div>
                                                <p className="text-sm font-black uppercase tracking-[0.2em] text-[#8a7d74]">Motivation pattern</p>
                                                <p className="mt-3 text-base leading-relaxed text-[var(--color-subtext)] brand-text">{report.partA.motivationSummary}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                                {report.riasecEntries.map((entry) => (
                                    <OrientationCard key={entry.code} entry={entry} />
                                ))}
                            </div>
                        </section>

                        <section className="rounded-[2rem] border border-[#efe6dd] bg-white/70 p-6 md:p-10 shadow-[0_22px_70px_rgba(0,0,0,0.06)] backdrop-blur-xl full-report-card">
                            <SectionHeading
                                eyebrow="Part B | Career choice"
                                title="Categorical career-choice resolution"
                                intro="Part B now resolves four direct outputs — Perspectives, Decision Making, Attitude, and Resources — and then uses Perspectives × Decision Making as the main career-choice driver, with Attitude and Resources acting as modifiers."
                            />

                            <div className="grid gap-4 grid-cols-[0.4fr_0.6fr] xl:items-start">
                                <div className="grid gap-4">
                                    <div className="rounded-[1.8rem] border border-[#f2dccf] bg-[linear-gradient(180deg,_#fff8f3_0%,_#ffffff_100%)] p-6 shadow-sm full-report-card">
                                        <div className="flex flex-wrap items-center gap-3">
                                            <span className="rounded-full bg-[#fff1e8] px-3 py-1 text-[11px] font-black uppercase tracking-[0.18em] text-[#F37741]">
                                                RAPD explainer
                                            </span>
                                            <span className="rounded-full bg-[#f6edfb] px-3 py-1 text-[11px] font-black uppercase tracking-[0.18em] text-[#92278F]">
                                                {report.behaviorStrengthLabel}
                                            </span>
                                        </div>

                                        <p className="mt-4 text-base leading-relaxed text-[var(--color-subtext)] brand-text">{report.partB.liveSummary}</p>
                                        <p className="mt-3 text-sm leading-relaxed text-[#57534e] brand-text">{report.partB.engineNote}</p>
                                    </div>

                                    <div className="rounded-[1.85rem] bg-gradient-to-br from-[#171717] via-[#23132c] to-[#3d1d43] p-6 text-white shadow-[0_18px_50px_rgba(0,0,0,0.14)]">
                                        <div className="flex flex-wrap items-center gap-3">
                                            <span className="rounded-full bg-white/10 px-3 py-1 text-[11px] font-black uppercase tracking-[0.18em] text-white/75">
                                                Future career
                                            </span>
                                            <span className="rounded-full bg-white/10 px-3 py-1 text-[11px] font-black uppercase tracking-[0.18em] text-white/75">
                                                {report.partB.recommendation.matrixKey}
                                            </span>
                                        </div>

                                        <h3 className="mt-6 text-3xl md:text-4xl font-black leading-tight brand-font">
                                            {report.partB.recommendation.title}
                                        </h3>
                                        <p className="mt-4 text-base md:text-lg leading-relaxed text-white/85 brand-text">
                                            {report.partB.recommendation.summary}
                                        </p>

                                        <div className="mt-6 grid gap-4 md:grid-cols-2">
                                            <div className="rounded-[1.35rem] border border-white/10 bg-white/5 p-4">
                                                <p className="text-[11px] font-black uppercase tracking-[0.18em] text-[#d6c6e1]">Foundation note</p>
                                                <p className="mt-2 text-sm leading-relaxed text-white/82 brand-text">{report.partB.recommendation.foundationNote}</p>
                                            </div>
                                            <div className="rounded-[1.35rem] border border-white/10 bg-white/5 p-4">
                                                <p className="text-[11px] font-black uppercase tracking-[0.18em] text-[#d6c6e1]">Resources note</p>
                                                <p className="mt-2 text-sm leading-relaxed text-white/82 brand-text">{report.partB.recommendation.resourcesNote}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="grid gap-4 md:grid-cols-2">
                                    {Object.values(resolvedChoices).map((choice) => (
                                        <ResolvedChoiceCard key={choice.code} choice={choice} />
                                    ))}
                                </div>
                            </div>
                        </section>

                        <section className="rounded-[2rem] border border-[#efe6dd] bg-white/70 p-6 md:p-10 shadow-[0_22px_70px_rgba(0,0,0,0.06)] backdrop-blur-xl full-report-card">
                            <SectionHeading
                                eyebrow="What to do next"
                                title="Turn insight into exposure and action"
                                intro="Keep the next step practical. The report becomes more useful when it turns into conversations, observation, small experiments, and visible capability-building."
                            />

                            <div className="grid gap-4 md:grid-cols-3">
                                {liveActionPlan.map((step) => (
                                    <ActionStepCard key={step.title} step={step} />
                                ))}
                            </div>

                            <div className="mt-8 rounded-[1.75rem] border border-[#eadff0] bg-[linear-gradient(135deg,_rgba(146,39,143,0.08),_rgba(243,119,65,0.08))] px-5 py-4 text-center shadow-sm full-report-card">
                                <p className="text-sm font-black uppercase tracking-[0.18em] text-[#92278F]">Guidance support</p>
                                <p className="mt-2 text-lg md:text-xl font-black text-[#171717] brand-font">Get in Touch with your Yancha Guide for Guidance</p>
                            </div>

                            <p className="mt-6 text-center text-sm italic text-gray-400 brand-text">{report.footerNote}</p>
                        </section>
                    </div>
                </div>
            </div>

            {printSurface}
        </>
    );
};

export default FullResultsPage;
